import React from 'react';
import { motion } from 'framer-motion';
import { Gift, Coins, TrendingUp, Star, Zap, Award, BookOpen } from 'lucide-react';
import { Progress } from '@/components/ui/progress';

const rewards = [
  { id: 1, title: 'Lesson Completion Points', points: 450, max: 500, icon: BookOpen, color: 'from-blue-400 to-blue-600', description: 'Earn 10 pts per lesson completed' },
  { id: 2, title: 'Quiz Bonus Points', points: 280, max: 300, icon: Star, color: 'from-primary to-yellow-500', description: 'Earn up to 30 pts per quiz based on score' },
  { id: 3, title: 'Assignment XP', points: 180, max: 500, icon: Zap, color: 'from-purple-400 to-purple-600', description: '50 pts per submitted assignment' },
  { id: 4, title: 'Attendance Streak', points: 90, max: 200, icon: TrendingUp, color: 'from-green-400 to-green-600', description: '10 pts per live class attended' },
];


const milestones = [
  { points: 250, label: 'Bronze Badge', color: 'text-orange-600', reached: true },
  { points: 500, label: 'Silver Badge', color: 'text-slate-500', reached: true },
  { points: 750, label: 'Gold Badge', color: 'text-yellow-500', reached: true },
  { points: 1000, label: 'Diamond Badge', color: 'text-blue-400', reached: false },
];

export default function CourseRewards() {
  const totalPoints = rewards.reduce((s, r) => s + r.points, 0);
  const maxPoints = rewards.reduce((s, r) => s + r.max, 0);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-2xl font-bold tracking-tight">Course Rewards</h2>
        <p className="text-muted-foreground text-sm mt-1">Earn XP and unlock milestones as you progress</p>
      </div>

      {/* Total XP Card */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        className="relative glass-yellow rounded-3xl border border-primary/25 p-6 overflow-hidden">
        <motion.div className="absolute top-0 right-0 w-48 h-48 rounded-full opacity-15 pointer-events-none"
          animate={{ scale: [1, 1.3, 1] }} transition={{ duration: 5, repeat: Infinity }}
          style={{ background: 'radial-gradient(circle, #FED202 0%, transparent 70%)', transform: 'translate(30%, -30%)' }} />
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-primary flex items-center justify-center shadow-xl shadow-primary/40">
            <Coins className="w-8 h-8 text-primary-foreground" />
          </div>
          <div>
            <p className="text-sm text-muted-foreground font-medium">Total XP Earned</p>
            <p className="font-heading font-black text-4xl tracking-tight text-foreground">{totalPoints.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground mt-0.5">of {maxPoints} possible XP</p>
          </div>
          <div className="ml-auto text-right">
            <div className="flex items-center gap-1 justify-end">
              <Award className="w-4 h-4 text-primary" />
              <span className="font-heading font-bold text-sm">Gold Tier</span>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">{1000 - totalPoints} XP to Diamond</p>
          </div>
        </div>
        <div className="mt-4">
          <div className="h-2.5 bg-primary/20 rounded-full overflow-hidden">
            <motion.div initial={{ width: 0 }} animate={{ width: `${(totalPoints / maxPoints) * 100}%` }}
              transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
              className="h-full bg-gradient-to-r from-primary to-yellow-400 rounded-full" />
          </div>
          <div className="flex justify-between mt-1.5 text-xs text-muted-foreground">
            <span>0 XP</span>
            <span className="font-semibold text-primary">{Math.round((totalPoints / maxPoints) * 100)}% complete</span>
            <span>{maxPoints} XP</span>
          </div>
        </div>
      </motion.div>

      {/* Milestones */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {milestones.map((m, i) => (
          <motion.div key={m.label} initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.08 }}
            className={`glass rounded-2xl border p-4 text-center ${m.reached ? 'border-primary/30 bg-primary/5' : 'border-white/80 opacity-50'}`}>
            <Gift className={`w-8 h-8 mx-auto mb-2 ${m.reached ? m.color : 'text-muted-foreground'}`} />
            <p className="text-xs font-bold">{m.label}</p>
            <p className={`text-[10px] mt-0.5 ${m.reached ? 'text-primary font-semibold' : 'text-muted-foreground'}`}>
              {m.reached ? '✓ Reached' : `${m.points} XP`}
            </p>
          </motion.div>
        ))}
      </div>

      {/* Individual reward categories */}
      <div className="grid sm:grid-cols-2 gap-4">
        {rewards.map((r, i) => {
          const Icon = r.icon;
          const pct = Math.round((r.points / r.max) * 100);
          return (
            <motion.div key={r.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 + 0.3 }}
              className="glass rounded-2xl border border-white/80 p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${r.color} flex items-center justify-center shadow-md`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-heading font-bold text-sm">{r.title}</p>
                  <p className="text-[11px] text-muted-foreground">{r.description}</p>
                </div>
                <span className="font-heading font-black text-base text-primary flex-shrink-0">{r.points}</span>
              </div>
              <div className="space-y-1">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{r.points}/{r.max} pts</span>
                  <span className="font-semibold">{pct}%</span>
                </div>
                <Progress value={pct} className="h-1.5" />
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}